/**
 * LightEffects Component
 *
 * Atmospheric light effects for dramatic visuals.
 * Features:
 * - Light rays from configurable direction
 * - Bokeh circles (soft, unfocused light points)
 * - Lens flare effects
 * - Ambient glow overlays
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, AbsoluteFill } from 'remotion';
import { seededRandom } from '../utils';

export type LightDirection = 'top' | 'top-left' | 'top-right' | 'left' | 'right' | 'center';

export interface LightRaysProps {
  /** Direction light comes from */
  direction?: LightDirection;
  /** Light color */
  color?: string;
  /** Intensity (0-1) */
  intensity?: number;
  /** Number of rays */
  rayCount?: number;
  /** Frame when effect starts */
  startFrame?: number;
  /** Enable ray animation */
  animate?: boolean;
}

/**
 * LightRays - Dramatic light beams
 */
export const LightRays: React.FC<LightRaysProps> = ({
  direction = 'top-left',
  color = 'rgba(255, 255, 255, 0.15)',
  intensity = 0.6,
  rayCount = 5,
  startFrame = 0,
  animate = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 1.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Calculate rotation based on direction
  const getRotation = (dir: LightDirection): number => {
    const rotations: Record<LightDirection, number> = {
      'top': 180,
      'top-left': 135,
      'top-right': 225,
      'left': 90,
      'right': 270,
      'center': 180,
    };
    return rotations[dir];
  };

  const baseRotation = getRotation(direction);

  // Subtle ray movement
  const rayAnimation = animate
    ? Math.sin(relativeFrame * 0.02) * 3
    : 0;

  return (
    <AbsoluteFill
      style={{
        pointerEvents: 'none',
        opacity: entranceOpacity * intensity,
        overflow: 'hidden',
      }}
    >
      {/* Main light rays container */}
      <div
        style={{
          position: 'absolute',
          top: direction.includes('top') ? '-50%' : direction === 'center' ? '0%' : '50%',
          left: direction.includes('left') ? '-20%' : direction.includes('right') ? '120%' : '50%',
          width: '200%',
          height: '200%',
          transform: `translate(-50%, -50%) rotate(${baseRotation + rayAnimation}deg)`,
        }}
      >
        {Array.from({ length: rayCount }).map((_, index) => {
          const angle = (index / rayCount) * 30 - 15; // Spread rays
          const opacity = 0.4 + Math.sin(index * 1.5) * 0.3;
          const width = 80 + index * 40;

          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${width}px`,
                height: '150%',
                background: `linear-gradient(to bottom, ${color}, transparent 80%)`,
                transform: `translateX(-50%) rotate(${angle}deg)`,
                opacity,
                transformOrigin: 'center top',
              }}
            />
          );
        })}
      </div>

      {/* Soft glow at light source */}
      <div
        style={{
          position: 'absolute',
          top: direction.includes('top') ? '-10%' : direction === 'center' ? '50%' : '110%',
          left: direction.includes('left') ? '-10%' : direction.includes('right') ? '110%' : '50%',
          width: '60%',
          height: '60%',
          background: `radial-gradient(ellipse at center, ${color}, transparent 70%)`,
          transform: 'translate(-50%, -50%)',
        }}
      />
    </AbsoluteFill>
  );
};

export interface BokehProps {
  /** Number of bokeh circles */
  count?: number;
  /** Primary color */
  color?: string;
  /** Intensity (0-1) */
  intensity?: number;
  /** Minimum size */
  minSize?: number;
  /** Maximum size */
  maxSize?: number;
  /** Start frame */
  startFrame?: number;
  /** Random seed */
  seed?: number;
}

/**
 * Bokeh - Soft unfocused light circles
 */
export const Bokeh: React.FC<BokehProps> = ({
  count = 12,
  color = 'rgba(255, 255, 255, 0.2)',
  intensity = 0.7,
  minSize = 40,
  maxSize = 150,
  startFrame = 0,
  seed = 33333,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  const random = seededRandom(seed);

  // Generate bokeh circles
  const circles = Array.from({ length: count }).map(() => ({
    x: random() * 100,
    y: random() * 100,
    size: minSize + random() * (maxSize - minSize),
    blur: 10 + random() * 20,
    opacity: 0.1 + random() * 0.15,
    phaseX: random() * Math.PI * 2,
    phaseY: random() * Math.PI * 2,
    speed: 0.5 + random() * 0.5,
  }));

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 1],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', overflow: 'hidden' }}>
      {circles.map((circle, index) => {
        // Gentle floating motion
        const driftX = Math.sin(relativeFrame * 0.015 * circle.speed + circle.phaseX) * 10;
        const driftY = Math.sin(relativeFrame * 0.01 * circle.speed + circle.phaseY) * 8;

        // Subtle pulsing
        const pulseScale = 1 + Math.sin(relativeFrame * 0.02 + index) * 0.05;

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${circle.x}%`,
              top: `${circle.y}%`,
              width: circle.size,
              height: circle.size,
              borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${color}, transparent 70%)`,
              opacity: circle.opacity * entranceOpacity * intensity,
              filter: `blur(${circle.blur}px)`,
              transform: `translate(-50%, -50%) translate(${driftX}px, ${driftY}px) scale(${pulseScale})`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

export interface LensFlareProps {
  /** Source position X (0-100%) */
  sourceX?: number;
  /** Source position Y (0-100%) */
  sourceY?: number;
  /** Flare color */
  color?: string;
  /** Intensity (0-1) */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
}

/**
 * LensFlare - Cinematic lens flare effect
 */
export const LensFlare: React.FC<LensFlareProps> = ({
  sourceX = 20,
  sourceY = 20,
  color = '#FFD700',
  intensity = 0.5,
  startFrame = 0,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Calculate flare elements along the axis from source to opposite corner
  const targetX = 100 - sourceX;
  const targetY = 100 - sourceY;

  // Flare elements at different positions along the axis
  const flareElements = [
    { t: 0.0, size: 80, opacity: 0.6 },
    { t: 0.2, size: 30, opacity: 0.3 },
    { t: 0.35, size: 20, opacity: 0.4 },
    { t: 0.5, size: 50, opacity: 0.2 },
    { t: 0.65, size: 15, opacity: 0.5 },
    { t: 0.8, size: 40, opacity: 0.25 },
    { t: 1.0, size: 60, opacity: 0.15 },
  ];

  // Subtle animation
  const pulse = 1 + Math.sin(relativeFrame * 0.05) * 0.1;

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', overflow: 'hidden' }}>
      {flareElements.map((element, index) => {
        const x = sourceX + (targetX - sourceX) * element.t;
        const y = sourceY + (targetY - sourceY) * element.t;

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: element.size * pulse,
              height: element.size * pulse,
              borderRadius: '50%',
              background: `radial-gradient(circle at center, ${color}, transparent 70%)`,
              opacity: element.opacity * entranceOpacity * intensity,
              transform: 'translate(-50%, -50%)',
              filter: 'blur(5px)',
            }}
          />
        );
      })}

      {/* Main source glow */}
      <div
        style={{
          position: 'absolute',
          left: `${sourceX}%`,
          top: `${sourceY}%`,
          width: 200,
          height: 200,
          background: `radial-gradient(circle at center, ${color}, transparent 60%)`,
          opacity: 0.4 * entranceOpacity * intensity,
          transform: `translate(-50%, -50%) scale(${pulse})`,
          filter: 'blur(20px)',
        }}
      />

      {/* Streak lines */}
      <div
        style={{
          position: 'absolute',
          left: `${sourceX}%`,
          top: `${sourceY}%`,
          width: 300,
          height: 4,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          opacity: 0.3 * entranceOpacity * intensity,
          transform: 'translate(-50%, -50%)',
          filter: 'blur(2px)',
        }}
      />
    </AbsoluteFill>
  );
};

export interface AmbientGlowProps {
  /** Glow color */
  color?: string;
  /** Position (or 'ambient' for full background) */
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'ambient';
  /** Intensity (0-1) */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
  /** Enable pulse animation */
  pulse?: boolean;
}

/**
 * AmbientGlow - Soft atmospheric glow
 */
export const AmbientGlow: React.FC<AmbientGlowProps> = ({
  color = 'rgba(255, 107, 53, 0.2)',
  position = 'ambient',
  intensity = 0.5,
  startFrame = 0,
  pulse = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 1],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Pulse animation
  const pulseValue = pulse
    ? 0.8 + Math.sin(relativeFrame * 0.03) * 0.2
    : 1;

  // Position-based gradient
  const getGradient = (): string => {
    switch (position) {
      case 'top':
        return `radial-gradient(ellipse 100% 60% at 50% 0%, ${color}, transparent 70%)`;
      case 'bottom':
        return `radial-gradient(ellipse 100% 60% at 50% 100%, ${color}, transparent 70%)`;
      case 'left':
        return `radial-gradient(ellipse 60% 100% at 0% 50%, ${color}, transparent 70%)`;
      case 'right':
        return `radial-gradient(ellipse 60% 100% at 100% 50%, ${color}, transparent 70%)`;
      case 'center':
        return `radial-gradient(ellipse 80% 80% at 50% 50%, ${color}, transparent 70%)`;
      case 'ambient':
      default:
        return `radial-gradient(ellipse 120% 100% at 50% 50%, ${color}, transparent 80%)`;
    }
  };

  return (
    <AbsoluteFill
      style={{
        pointerEvents: 'none',
        background: getGradient(),
        opacity: entranceOpacity * intensity * pulseValue,
      }}
    />
  );
};

/**
 * CombinedLightEffects - All light effects combined for easy use
 */
export interface CombinedLightEffectsProps {
  /** Enable light rays */
  enableRays?: boolean;
  /** Enable bokeh */
  enableBokeh?: boolean;
  /** Enable ambient glow */
  enableGlow?: boolean;
  /** Primary color */
  color?: string;
  /** Overall intensity */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
}

export const CombinedLightEffects: React.FC<CombinedLightEffectsProps> = ({
  enableRays = true,
  enableBokeh = true,
  enableGlow = true,
  color = 'rgba(255, 255, 255, 0.15)',
  intensity = 0.6,
  startFrame = 0,
}) => {
  return (
    <>
      {enableGlow && (
        <AmbientGlow
          color={color}
          position="ambient"
          intensity={intensity * 0.5}
          startFrame={startFrame}
          pulse={true}
        />
      )}
      {enableRays && (
        <LightRays
          direction="top-left"
          color={color}
          intensity={intensity}
          rayCount={5}
          startFrame={startFrame}
          animate={true}
        />
      )}
      {enableBokeh && (
        <Bokeh
          count={8}
          color={color}
          intensity={intensity}
          minSize={50}
          maxSize={120}
          startFrame={startFrame}
        />
      )}
    </>
  );
};
